// @ts-nocheck

import { colors } from '@lmc/common/constants'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { find } from 'lodash'
import PropTypes from 'prop-types'
import { Component } from 'react'
import { useQueryClient } from 'react-query'
import { connect } from 'react-redux'
import Actions from '../../../actions'
import { mixpanel } from '../../../common/services/analytics'
import Operations from '../../../operations'
import { withRouter } from '../../../withRouter'
import { Step, StepList } from '../../generic'
import Modal from '..'
import { EmarMedicationResidentInfo, EmarMedicationTitle } from './components'
import {
  AddMedDetailsStep,
  AddMedInstructionsStep,
  AddMedNameStep,
  AddMedSaveStep,
  AddMedScheduleStep
} from './steps'

const MODAL_ID = 'addEmarMedication'

interface AddEmarMedicationModalProps {
  close: () => void
  fetchData: () => void
  fields: any
  isOpen: boolean
  resident: any
  validateFormFields: (data: any) => void
  formState: any
  emarV2ChartEnabled: boolean
  testId?: string
}

const Subheader = (props: {
  fields: any
  formState: { medicationName: string }
  resident: any
}) => {
  const { fields, formState, resident } = props

  return (
    <div style={styles.subheaderContainer}>
      <EmarMedicationResidentInfo resident={resident} />
      <EmarMedicationTitle
        fields={fields}
        medicationName={formState.medicationName}
      />
    </div>
  )
}

/**
 * Renders the Add eMAR Medication modal.
 */
class AddEmarMedicationModal extends Component<AddEmarMedicationModalProps> {
  render() {
    const {
      close,
      fetchData,
      fields,
      isOpen,
      resident,
      validateFormFields,
      testId,
      ...formProps
    } = this.props

    if (!isOpen) {
      return null
    }

    return (
      <div
        style={styles.container}
        data-testid={
          testId
            ? `${testId}-add-emar-medication-modal`
            : 'add-emar-medication-modal'
        }
      >
        <StepList
          closeLabel="Cancel"
          onClose={close}
          stepLabels={STEP_LABELS}
          stepsSubheader={
            <Subheader
              fields={fields}
              formState={this.props.formState}
              resident={resident}
            />
          }
          useAntdSteps
        >
          <Step
            formId={MODAL_ID}
            component={AddMedNameStep}
            resident={resident}
            fields={fields}
            stepStyles={styles.step}
            testId={testId}
            {...formProps}
          />
          <Step
            formId={MODAL_ID}
            component={AddMedScheduleStep}
            resident={resident}
            fields={fields}
            stepStyles={styles.step}
            validateFormFields={validateFormFields}
            testId={testId}
            {...formProps}
          />
          <Step
            formId={MODAL_ID}
            component={AddMedInstructionsStep}
            fields={fields}
            resident={resident}
            stepStyles={styles.scrollStep}
            validateFormFields={validateFormFields}
            testId={testId}
            {...formProps}
          />
          <Step
            formId={MODAL_ID}
            component={AddMedDetailsStep}
            fields={fields}
            resident={resident}
            stepStyles={styles.scrollStep}
            validateFormFields={validateFormFields}
            testId={testId}
            {...formProps}
          />
          <Step
            component={AddMedSaveStep}
            fields={fields}
            fetchData={fetchData}
            formId={MODAL_ID}
            resident={resident}
            stepStyles={styles.scrollStep}
            testId={testId}
            {...formProps}
          />
        </StepList>
      </div>
    )
  }
}

const STEP_LABELS = ['Name', 'Schedule', 'Instructions', 'Details', 'Save']

const styles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    margin: '0 auto',
    zIndex: 100,
    background: colors.background
  },
  modalTitle: {
    fontSize: 28,
    fontWeight: 300,
    lineHeight: '36px',
    marginBottom: 48
  },
  scrollStep: {
    paddingTop: 0
  },
  step: {
    paddingTop: 225
  },
  subheaderContainer: {
    margin: 'auto',
    marginTop: 25,
    maxWidth: 960,
    padding: '0px 10px'
  }
}

AddEmarMedicationModal.propTypes = {
  close: PropTypes.func,
  fetchData: PropTypes.func.isRequired,
  fields: PropTypes.object,
  isOpen: PropTypes.bool,
  resident: PropTypes.object,
  validateFormFields: PropTypes.func.isRequired,
  testId: PropTypes.string
}

const mapStateToProps = (state, props) => ({
  eMARStockEnabled: state.global.home.features?.eMARStock?.enabled,
  resident: find(state.data.residents, { id: props.router.params.id })
})

const mapDispatchToProps = (dispatch: any, ownProps: any) => ({
  fetchData: () => {
    const emarV2ChartEnabled = ownProps?.emarV2ChartEnabled === true
    dispatch(
      Operations.loadEmarRoundTimings({
        useV2Endpoints: ownProps?.emarV2RoundTimings === true,
        legacyUrl: emarV2ChartEnabled
          ? '/emar/legacy/emar-round-timings'
          : undefined
      })
    )
    // dispatch(Operations.loadList('emar-rounds'));
    if (emarV2ChartEnabled) {
      dispatch(
        Operations.loadList(
          'emar-resident-medications',
          undefined,
          true,
          '/emar/medication-schedule-patterns/legacy/emar-resident-medications'
        )
      )
    } else {
      dispatch(Operations.loadList('emar-resident-medications'))
    }
  },
  openNewRoundTimingModal: () => {
    dispatch(Actions.openModal({ modalId: 'addEmarRoundTimingModal' }))
    dispatch(Actions.addForm({ formId: 'addEmarRoundTimingModal' }))
  },
  validateFormFields: (data: any) =>
    dispatch(Operations.validateFormFields({ ...data, formId: MODAL_ID })),
  mixpanel
  // fetchDailyTodos: () => dispatch(Operations.fetchDailyTodos()),
})

const wrappedModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(AddEmarMedicationModal)

const ConnectedAddEmarMedicationModal = withRouter(
  Modal(wrappedModal, MODAL_ID)
)

const AddEmarMedicationModalWithFeatureFlag = (props: any) => {
  const flags = useFeatureFlags({
    flagNames: ['emar_v2_chart', 'emar_v2_round_timings']
  })?.data
  const emarV2ChartEnabled = flags?.emar_v2_chart?.enabled ?? false
  const emarV2RoundTimings = flags?.emar_v2_round_timings?.enabled ?? false
  const queryClient = useQueryClient()

  return (
    <ConnectedAddEmarMedicationModal
      {...props}
      emarV2ChartEnabled={emarV2ChartEnabled}
      emarV2RoundTimings={emarV2RoundTimings}
      queryClient={queryClient}
    />
  )
}

export default AddEmarMedicationModalWithFeatureFlag
